import React, { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SearchBarComponent from "src/modules/admin/components/search-bar/search-bar.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import {
  getAssetByCode,
  getSearchedAssets,
  setSearchedAssets,
  setSelectedAssetCode,
} from "src/store/slices/asset.slice";

interface DashboardFloorplanWildcardSearchingComponentProps {
  isUpdateFloorPlan?: boolean;
  setSelectedSearchedAssetCodeForHighlighting?: React.Dispatch<
    React.SetStateAction<string>
  >;
  searchBarLength?: string;
}

const DashboardFloorplanWildcardSearchingComponent: FC<
  DashboardFloorplanWildcardSearchingComponentProps
> = ({
  isUpdateFloorPlan = false,
  setSelectedSearchedAssetCodeForHighlighting,
  searchBarLength,
}: any) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const searchBoxRef = useRef<HTMLDivElement>(null);

  const params: any = useParams();
  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;

  const searchedAssets = useSelector(
    (state: any) => state.asset.searchedAssets
  );

  const searchResultsHeight = Math.min(searchedAssets?.length * 70, 400);

  const [showSearchBox, setShowSearchBox] = useState<boolean>(false);
  const [clearSearchResult, setClearSearchResult] = useState(false);

  const [noResult, setNoResult] = useState(false);
  const [isSearchBarEmpty, setIsSearchBarEmpty] = useState(true);
  const [isAlreadyDispatched, setIsAlreadyDispatched] = useState(false);

  useEffect(() => {
    // Reset searchedAssets to an empty array on page load
    dispatch(setSearchedAssets([]));
  }, [dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const assetCodeParam = urlParams.get("assetCode");
    const assetCode = assetCodeParam ? safeAtob(assetCodeParam) : null;

    if (assetCode) {
      // Add a small delay to ensure the floor plan is ready for highlighting
      setTimeout(() => {
        // highlight the asset on the floor plan (decoded from base64)
        setSelectedSearchedAssetCodeForHighlighting?.(assetCode);
        dispatch(setSelectedAssetCode(assetCode));
      }, 500);
    } /*else {
      // Clear highlighting when no assetCode in URL or page refresh without assetCode
      setSelectedSearchedAssetCodeForHighlighting?.("");
      dispatch(setSelectedAssetCode(""));
    }*/
  }, [location.search, setSelectedSearchedAssetCodeForHighlighting, dispatch]);

  const searchByAsset = (assetCode: any) => {
    setShowSearchBox(false);
    setClearSearchResult(true);
    setNoResult(false);
    setIsAlreadyDispatched(false);

    setSelectedSearchedAssetCodeForHighlighting((pre: any) => {
      if (pre === assetCode && isUpdateFloorPlan) {
        toast.warning("Asset Already Annotated", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnFocusLoss: true,
          draggable: true,
          pauseOnHover: true,
        });

        return assetCode;
      } else {
        return assetCode;
      }
    });

    dispatch(getAssetByCode({ id: assetCode })).then((res: any) => {
      if (!isUpdateFloorPlan) {
        const searchedFloor = res?.payload?.blockToFloorCode;
        const sensorName = res?.payload?.sensorDisplayName;
        if (!searchedFloor) {
          toast.warning(`${sensorName}'s floorCode is null`, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnFocusLoss: true,
            draggable: true,
            pauseOnHover: true,
          });
        } else {
          const newPath = location.pathname.replace(
            /floorplan\/[^/]+/,
            `floorplan/${encodeURIComponent(searchedFloor)}`
          );

          navigate(newPath);
        }
      }

      dispatch(setSelectedAssetCode(assetCode));
      dispatch(setSearchedAssets([]));
    });
  };

  useEffect(() => {
    if (searchedAssets?.length) {
      setShowSearchBox(true);
      setClearSearchResult(false);
      setNoResult(false);
    }
  }, [searchedAssets]);

  useEffect(() => {
    if (isAlreadyDispatched && searchedAssets?.length === 0) {
      setNoResult(true);
    }
  }, [isAlreadyDispatched, searchedAssets]);

  useEffect(() => {
    if (isSearchBarEmpty) {
      setShowSearchBox(false);
      setNoResult(false);
    }
  }, [isSearchBarEmpty]);

  const handleClickOutside = (event: any) => {
    if (
      searchBoxRef.current &&
      !searchBoxRef.current.contains(event.target as Node)
    ) {
      setClearSearchResult(false);
      setShowSearchBox(false);
      setNoResult(false);
      setIsAlreadyDispatched(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // useEffect(() => {
  //   if (!assetDetails?.floor) return;

  //   const newPath = location.pathname.replace(
  //     /floorplan\/L\d+/,
  //     `floorplan/${assetDetails?.floor}`
  //   );

  //   navigate(newPath);
  // }, [assetDetails, location.pathname, navigate]);

  return (
    <>
      <SearchBarComponent
        placeholder="Search by Asset (Type three or more characters to search)"
        methodToDispatch={getSearchedAssets}
        isDynamicSearch={true}
        clearSearchResult={clearSearchResult}
        extraParameter={blockCode}
        isUpdateFloorPlan={isUpdateFloorPlan}
        setIsAlreadyDispatched={setIsAlreadyDispatched}
        setIsSearchBarEmpty={setIsSearchBarEmpty}
        isFloorPlan={true}
        searchBarLength={searchBarLength}
      />

      {searchedAssets && searchedAssets.length > 0 && showSearchBox && (
        <div
          ref={searchBoxRef}
          className="shadow-sm"
          style={{
            backgroundColor: "#ffffff",
            height: `${searchResultsHeight}px`,
            overflowY: "auto",
            position: "absolute",
            top: 40,
            left: 10,
            zIndex: 999,
            width: "95%",
            borderBottomRightRadius: "15px",
            borderBottomLeftRadius: "15px",
          }}
        >
          {searchedAssets.map((asset: any) => (
            <button
              key={asset.assetCode}
              className="search-item"
              onClick={() => searchByAsset(asset?.assetCode)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="mb-1">{asset.assetCode}</div>
                  <div className="info">
                    <span className="fw-bold info-level-color">
                      Display Name:{" "}
                    </span>
                    {asset.sensorDisplayName}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )} 
      {noResult && !showSearchBox && (
        <div
          style={{
            marginTop: "1rem",
            backgroundColor: "#ffffff",
            height: `40px`,
            overflowY: "auto",
            position: "absolute",
            top: 40,
            left: 10,
            zIndex: 999,
            width: "95%",
            borderBottomRightRadius: "15px",
            borderBottomLeftRadius: "15px",
            paddingLeft: "1rem",
          }}
        >
          <strong>No result found</strong>
        </div>
      )}
    </>
  );
};

export default DashboardFloorplanWildcardSearchingComponent;
